import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { createUserStory } from "./userStoryService";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";

const USER_STORY_STATUSES = ["Not Started", "In Progress", "Completed"];
const USER_STORY_PRIORITIES = ["High", "Medium", "Low"];

function AddUserStory({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeProjectId, subprojectId: routeSubprojectId } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [formData, setFormData] = useState({
    projectId: routeProjectId ? String(routeProjectId) : "",
    subProjectId: routeSubprojectId ? String(routeSubprojectId) : "",
    title: "",
    description: "",
    status: "Not Started",
    priority: ""
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/projects");
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.projects ?? [];
        setProjects(arr || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!formData.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    const pid = Number(formData.projectId);
    axiosInstance
      .get(`/sub-projects/project/${pid}`)
      .then(async (res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = [];
        let primaryParsed = false;
        if (Array.isArray(raw)) {
          arr = raw;
          primaryParsed = true;
        } else if (raw && Array.isArray(raw.sub_projects)) {
          arr = raw.sub_projects;
          primaryParsed = true;
        } else if (raw && Array.isArray(raw.data)) {
          arr = raw.data;
          primaryParsed = true;
        }
        if (!primaryParsed) {
          try {
            const legacy = await axiosInstance.get(`/projects/${pid}/sub-projects`);
            if (cancelled) return;
            const lr = legacy.data;
            arr = Array.isArray(lr) ? lr : lr?.sub_projects ?? lr?.data ?? [];
          } catch {
            arr = [];
          }
        }
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setSubprojects(mapped);
        if (mapped.length === 0) {
          setFormData((f) => ({ ...f, subProjectId: "" }));
        }
      })
      .catch(() => {
        if (!cancelled) setSubprojects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubprojects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.projectId]);

  useEffect(() => {
    if (loadingSubprojects || !formData.projectId || !formData.subProjectId) return;
    const ok = subprojects.some((sp) => String(sp.id) === String(formData.subProjectId));
    if (!ok) setFormData((f) => ({ ...f, subProjectId: "" }));
  }, [loadingSubprojects, formData.projectId, formData.subProjectId, subprojects]);

  const showSubprojectField =
    Boolean(formData.projectId) && (loadingSubprojects ? true : subprojects.length > 0);

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, projectId: val, subProjectId: "" }));
  };

  const handleSubprojectChange = (e) => {
    setFormData((f) => ({ ...f, subProjectId: e.target.value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const goBack = () => {
    const returnToPath = location.state?.returnToPath;
    const returnToSearch = location.state?.returnToSearch || "";
    const returnToState = location.state?.returnToState;
    if (returnToPath) {
      navigate(`${returnToPath}${returnToSearch}`, { state: returnToState || undefined });
      return;
    }
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    const effectiveProjectId = formData.projectId ? Number(formData.projectId) : null;
    if (!effectiveProjectId || !Number.isFinite(effectiveProjectId)) {
      setError("Please select a project.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      let createdBy = 1;
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
      const uiStatus = formData.status || "Not Started";
      const backendStatus = uiStatus === "Not Started" ? "Not Started" : uiStatus;
      const payload = {
        Project_Id: effectiveProjectId,
        Title: title,
        Description: (formData.description || "").trim(),
        Status: backendStatus,
        Priority: formData.priority || undefined,
        Priority_Level: formData.priority || undefined,
        Created_By: createdBy
      };
      if (formData.subProjectId) {
        payload.Sub_Project_Id = Number(formData.subProjectId);
      } else {
        payload.Sub_Project_Id = null;
      }
      await createUserStory(payload);
      try {
        sessionStorage.setItem("refresh_user_stories_list", "1");
      } catch {
        /* ignore */
      }
      alert("User story created successfully.");
      goBack();
    } catch (err) {
      console.error("Error creating user story:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to create user story.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-form-container editcustomer-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <div className="issues-main-card">
        <form className="editcustomer-form edituserstory-form" onSubmit={handleSubmit}>
          <div className="form-section">
            {error && (
              <div className="userstories-add-error" style={{ marginBottom: 16 }}>
                {error}
              </div>
            )}
            <div className="form-grid addtask-form-grid">
              <div className="form-group">
                <label>
                  Project <span className="required">*</span>
                </label>
                <select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleProjectChange}
                  required
                >
                  <option value="">Select project</option>
                  {projects.map((p) => {
                    const pid = p.Project_Id ?? p.project_id ?? p.id;
                    const name = p.Project_Name ?? p.project_name ?? p.name ?? "";
                    return (
                      <option key={pid} value={String(pid)}>
                        {name || `Project ${pid}`}
                      </option>
                    );
                  })}
                </select>
              </div>
              {showSubprojectField && (
                <div className="form-group">
                  <label>Subproject</label>
                  <select
                    name="subProjectId"
                    value={formData.subProjectId}
                    onChange={handleSubprojectChange}
                    disabled={loadingSubprojects}
                  >
                    <option value="">None (project-level)</option>
                    {subprojects.map((sp) => (
                      <option key={sp.id} value={String(sp.id)}>
                        {sp.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Story title"
                  required
                />
              </div>
              <div className="form-group addtask-description-full">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Story description"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select name="priority" value={formData.priority} onChange={handleInputChange}>
                  <option value="">Select priority</option>
                  {USER_STORY_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {USER_STORY_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions editcustomer-actions">
              <button type="button" className="btn-subproject-cancel" onClick={goBack} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn-subproject-primary" disabled={submitting}>
                {submitting ? "Creating..." : "Create User Story"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddUserStory;
